import axios from 'axios';
import { getAuthToken, removeAuthToken } from 'utils/storage';
import { isTokenExpired } from 'utils/tokenExpiry';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Send current organizationId in header for multi-tenant support
    const currentOrganizationId = localStorage.getItem('currentOrganizationId');
    if (currentOrganizationId && currentOrganizationId !== 'synthetic-org') {
      config.headers['X-Organization-Id'] = currentOrganizationId;
    }

    /**
     * A file upload must not go out as JSON.
     *
     * This instance defaults every request to `application/json`, and axios
     * reads that as an INSTRUCTION rather than a leftover: given FormData and a
     * JSON content type it serialises the form to JSON and sends that. The file
     * never leaves the browser, and the server answers
     * `415 Content-Type 'application/json' is not supported` — which reads as a
     * server fault, and cost a production afternoon.
     *
     * Clearing it here lets the browser set `multipart/form-data` WITH the
     * boundary, which is the part a hand-written header always gets wrong.
     * Done centrally because the alternative — remembering the override at
     * every call site — had already been forgotten at four of them, including
     * both campaign builders' CSV upload.
     */
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers as any;
      // AxiosHeaders normalises its keys internally, so `delete h['Content-Type']`
      // can leave the stored `content-type` behind. Its own API is the one that
      // actually removes it.
      if (typeof h?.delete === 'function') h.delete('Content-Type');
      else { delete h['Content-Type']; delete h['content-type']; }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // Handle authentication and authorization errors
    if (status === 401) {
      // 401 Unauthorized: User is not authenticated (token missing/expired/invalid)
      console.log('🔒 Authentication error: Session expired or invalid token');
      removeAuthToken();
      // Only navigate if we are NOT already on the login page. Assigning
      // location.href to the CURRENT url reloads it, so a component that
      // renders on /login and calls the API (the softphone widget is mounted
      // outside the routes and does exactly that) produced: mount -> 401 ->
      // reload -> mount -> 401. Measured at 102 requests in a few seconds,
      // which is the "blinking" — the whole page reloading over and over.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (status === 403) {
      // 403 Forbidden usually means authenticated-but-not-permitted, and logging
      // those out would throw somebody back to the login page for clicking a
      // button they lack rights to. So a 403 alone is NOT a logout.
      //
      // BUT the backends disagree about expiry. Verified 2026-09-08 with a
      // missing, a malformed and an expired token:
      //
      //   api-staging.greengiraffe.in  ->  401, 401, 401
      //   api-tvoice.teeoff.tech       ->  403, 403, 403
      //
      // On the tvoice backend — which the Africa portal talks to — an expired
      // token IS a 403, so this branch kept a dead token forever: every request
      // failed and the user was never logged out.
      //
      // Decided from the TOKEN rather than the status, so it is right on both
      // backends and a genuine permission error held with a valid token still
      // does not log anyone out.
      if (isTokenExpired(getAuthToken())) {
        console.log('🔒 Session expired (403 from a backend that does not use 401)');
        removeAuthToken();
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      console.log('🚫 Authorization error: Insufficient permissions for this action');
      // Don't redirect to login - let the UI handle showing an error message
      return Promise.reject(error);
    }

    // Handle other HTTP errors
    if (status === 400) {
      // 400 Bad Request: Business logic errors (validation, already exists, invalid input, etc.)
      console.log('⚠️ Bad Request:', error.response?.data?.message || 'Invalid request');
    } else if (status === 404) {
      // 404 Not Found: Resource not found
      console.log('🔍 Not Found:', error.response?.data?.message || 'Resource not found');
    } else if (status === 500) {
      // 500 Internal Server Error: Server-side error
      console.error('💥 Server Error:', error.response?.data?.message || 'Internal server error');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;